<template>
  <div class="review-checked-items">
    <!-- Header -->
    <header class="app-header">
      <!-- Left section: Logo and User Info -->
      <div class="header-left">
        <div class="logo-container">
          <img
            src="@/assets/StaffTrainingLogo.png"
            alt="Company Logo"
            class="company-logo"
          >
        </div>
        <div class="user-info">
          Basket
        </div>
      </div>

      <!-- Right section: Admin and Logout Buttons -->
      <div class="header-right">
        <div
          v-if="isAdmin"
          class="admin-navigation"
        >
          <button
            class="admin-button"
            @click="navigateToAdminDashboard"
          >
            <span class="admin-text">Admin</span>
            <span class="admin-icon">➡️</span>
          </button>
        </div>
        <div class="logout-container">
          <button
            class="logout-button"
            @click="handleLogout"
          >
            Logout
          </button>
        </div>
      </div>
    </header>

    <h2>Items You Have Checked In/Out</h2>

    <div v-if="checkedItems.length > 0">
      <table>
        <thead>
          <tr>
            <th>Item Image</th>
            <th>Item Name</th>
            <th>Quantity</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in checkedItems"
            :key="item.itemID"
          >
            <td>
              <img
                v-if="item.imageUrl"
                :src="item.imageUrl"
                alt="Item Thumbnail"
                class="item-thumbnail"
              >
            </td>
            <td>{{ item.name }}</td>
            <td>{{ item.quantity }}</td>
            <td>
              <button @click="handleRemove(item.itemID)">
                Remove
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else>
      <p>You haven't checked any items yet.</p>
    </div>

    <div class="button-group">
      <button @click="goBackToQrScanner">
        Scan More Items
      </button>
      <button @click="handleFinishedScanning">
        Finished Scanning
      </button>
    </div>

    <!-- Footer -->
    <footer class="app-footer">
      <div class="footer-content">
        <img
          src="@/assets/ParentLogo.png"
          alt="Parent Company Logo"
          class="footer-logo"
        >
        <p>© 2024 Staff Nursing Ltd. All rights reserved.</p>
      </div>
    </footer>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import axios from 'axios';

export default {
  name: 'ReviewCheckedItems',
  data() {
    return {
      isAdmin: false, // Dynamically set based on user role
    };
  },
  computed: {
    ...mapGetters(['getCheckedItems']), // Get checked items from Vuex store

    // Rename 'getCheckedItems' getter as 'checkedItems' for convenience
    checkedItems() {
      return this.getCheckedItems;
    },
  },
  async created() {
    // Fetch the user role and set isAdmin during component creation
    await this.checkUserRole();
  },
  methods: {
    ...mapActions(['removeItem', 'resetCheckedItems']), // Map Vuex actions

    async checkUserRole() {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          console.error('No auth token found.');
          return;
        }

        // Verify the token with the backend to get the user's role
        const response = await axios.post('https://api.inventory.stafftraining.me/api/verifyToken', {}, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data && response.data.role) {
          this.isAdmin = response.data.role === 'admin';
        } else {
          console.error('Unable to determine user role from response:', response.data);
        }
      } catch (error) {
        console.error('Error verifying user role:', error);
      }
    },

    async handleRemove(itemId) {
      const itemToRemove = this.checkedItems.find((item) => item.itemID === itemId);

      if (!itemToRemove) {
        console.error('Item not found in checked items.');
        return;
      }

      // Confirm before removing
      // eslint-disable-next-line no-alert
      const confirmed = window.confirm('Are you sure you want to remove this item?');
      if (!confirmed) return;

      try {
        const response = await axios.post('https://api.inventory.stafftraining.me/api/updateStock', {
          itemID: itemId,
          action: 'checkIn', // Assuming 'checkIn' action to revert check-out
          quantity: itemToRemove.quantity,
        }, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        });

        if (response.data && response.data.success) {
          this.removeItem(itemId); // Remove item from Vuex store
        } else {
          // eslint-disable-next-line no-alert
          alert('Error: Could not update inventory. Please try again.');
        }
      } catch (error) {
        console.error('Error updating inventory:', error);
        // eslint-disable-next-line no-alert
        alert('An error occurred while updating the inventory.');
      }
    },

    handleLogout() {
      localStorage.removeItem('authToken'); // Remove auth token
      this.resetCheckedItems(); // Reset the Vuex store
      this.$router.push({ name: 'Login' }); // Redirect to login page
    },

    handleFinishedScanning() {
      this.resetCheckedItems(); // Clear the Vuex store
      this.handleLogout(); // Log out the user
    },

    goBackToQrScanner() {
      this.$router.push({ name: 'QrScanner', query: { startScanning: true } });
    },

    navigateToAdminDashboard() {
      this.resetCheckedItems(); // Clear checked items
      this.$router.push({ name: 'AdminDashboard' }); // Redirect to Admin Dashboard
    },
  },
};
</script>

  <style scoped>
  .review-checked-items {
    padding: 20px;
    padding-top: 140px; /* Add padding to accommodate the fixed header */
    padding-bottom: 160px; /* Add padding to accommodate the fixed footer */
  }

  h2 {
    font-size: 24px;
    margin-bottom: 20px;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 20px;
  }

  th, td {
    border: 1px solid #ddd;
    padding: 8px;
    text-align: center;
  }

  th {
    background-color: #f2f2f2;
  }

  button {
    background-color: #00A3DB;
    color: white;
    border: none;
    width:auto;
    padding: 8px 12px;
    cursor: pointer;
    border-radius: 12px;
  }

  button:hover {
    background-color: #2980b9;
  }

  .item-thumbnail {
    width: 50px;
    height: 50px;
    object-fit: cover;
    border-radius: 4px;
  }

  /* Styles for header */
  .app-header {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100px;
    background-color: #262626;
    color: white;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 20px;
    box-sizing: border-box;
    z-index: 10;
  }

  /* Left section: Logo and User Info */
  .header-left {
    display: flex;
    align-items: center;
  }
  .logo-container {
    display: flex;
    align-items: center;
  }

  .company-logo {
    height: 64px;
  }
  .user-info {
    color: var(--text-light-color);
    font-size: 0.9em;
    margin-left: 20px; /* Spacing between logo and user info */
  }
  /* Right section: Admin and Logout buttons */
  .header-right {
    display: flex;
    align-items: center;
  }

  .logout-button {
    background-color: #00A3DB;
    color: white;
    border: none;
    border-radius: 12px;
    padding: 8px 16px;
    cursor: pointer;
    transition: background-color 0.3s ease;
  }

  .logout-button:hover {
    background-color: #00dbba;
  }

  .admin-navigation {
    display: flex;
    align-items: center;
    margin-right: 10px;
  }

  .admin-button {
    background-color: #00A3DB;
    color: white;
    border: none;
    border-radius: 12px;
    padding: 8px 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    overflow: hidden;
    box-sizing: border-box;
  }

  .admin-icon {
    display: inline-block;
    vertical-align: middle;
  }

  .button-group {
  display: flex;
  justify-content: left; /* Center the buttons horizontally */
  gap: 20px; /* Add space between the buttons */
  margin-top: 20px; /* Optional: Add space above the button group */
  }

  /* Styles for footer */
  .app-footer {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 120px;
    background-color: #262626;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    box-sizing: border-box;
    z-index: 10;
    padding: 10px;
    text-align: center;
  }

  .footer-logo {
    max-width: 100%;
    height: auto;
    max-height: 60px;
    margin-bottom: 10px;
    align-self: center;
  }

  .app-footer p {
    margin: 0;
    font-size: 12px;
    text-align: center;
  }
  </style>
